import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { features } from '@/config/flavor';

/**
 * i18n Configuration
 *
 * Initializes i18next with:
 *   - HTTP backend loading translations from /locales/{lng}/{ns}.json
 *   - Browser language detection (localStorage → navigator)
 *   - React integration via react-i18next
 *
 * Namespaces: common, dashboard, positions, members, shifts, schedule, timeline, settings
 */

const NAMESPACES = [
  'common',
  'dashboard',
  'positions',
  'members',
  'shifts',
  'schedule',
  'timeline',
  'settings',
  'info',
] as const;

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: features.defaultLocale,
    supportedLngs: features.supportedLocales,
    defaultNS: 'common',
    ns: [...NAMESPACES],

    backend: {
      loadPath: (lngs: string[] | string, namespaces: string[] | string) => {
        const lng = Array.isArray(lngs) ? lngs[0] : lngs;
        const ns = Array.isArray(namespaces) ? namespaces[0] : namespaces;
        
        if (features.isIdf && lng === 'he') {
          return `/locales/he-idf/${ns}.json`;
        }
        return `/locales/${lng}/${ns}.json`;
      },
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'ezshift-language',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: true,
    },
  });

/**
 * Sets the document direction and lang attribute based on language.
 * Called on language change.
 */
export function updateDocumentDirection(lng: string): void {
  const dir = lng === 'he' || lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lng);
}

// Update direction on language change
i18n.on('languageChanged', updateDocumentDirection);

// Set initial direction
if (i18n.language) {
  updateDocumentDirection(i18n.language);
}

export default i18n;
